/**
 * PagePopupManager
 *
 * Fetches and displays page-specific popups created via the admin panel.
 * Renders each active popup as a dismissible modal card.
 *
 * show_once = true  → dismissed for the entire browser session (sessionStorage)
 * show_once = false → re-appears on every visit
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Info, AlertTriangle, Megaphone, Wrench, ArrowRight } from 'lucide-react';
import API_URL from '../config/api';

const TYPE_CONFIG = {
  info: {
    icon: Info,
    accent: '#3B82F6',
    accentRgb: '59,130,246',
    bg: 'rgba(59,130,246,0.07)',
    border: 'rgba(59,130,246,0.22)',
    label: 'Info',
  },
  warning: {
    icon: AlertTriangle,
    accent: '#F59E0B',
    accentRgb: '245,158,11',
    bg: 'rgba(245,158,11,0.07)',
    border: 'rgba(245,158,11,0.22)',
    label: 'Warning',
  },
  maintenance: {
    icon: Wrench,
    accent: '#EF4444',
    accentRgb: '239,68,68',
    bg: 'rgba(239,68,68,0.07)',
    border: 'rgba(239,68,68,0.22)',
    label: 'Maintenance',
  },
  announcement: {
    icon: Megaphone,
    accent: '#10B981',
    accentRgb: '16,185,129',
    bg: 'rgba(16,185,129,0.07)',
    border: 'rgba(16,185,129,0.22)',
    label: 'Announcement',
  },
};

function SinglePopup({ popup, onClose, index }) {
  const cfg = TYPE_CONFIG[popup.type] || TYPE_CONFIG.info;
  const Icon = cfg.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 24 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 16 }}
      transition={{ type: 'spring', stiffness: 340, damping: 28, delay: index * 0.07 }}
      className="relative rounded-2xl max-w-md w-full mx-auto overflow-hidden"
      style={{
        background: 'linear-gradient(150deg, #0d1424 0%, #0f1520 60%, #0a0f1a 100%)',
        border: `1.5px solid ${cfg.border}`,
        boxShadow: `0 0 48px rgba(${cfg.accentRgb},0.12), 0 20px 48px rgba(0,0,0,0.55)`,
      }}
    >
      {/* Shimmer top stripe */}
      <div className="relative h-0.5 w-full overflow-hidden">
        <div className="h-full w-full" style={{ background: cfg.accent }} />
        <motion.div
          className="absolute inset-y-0 w-1/3"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)' }}
          animate={{ x: ['-100%', '400%'] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
        />
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-gray-600 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10 z-10"
        aria-label="Close popup"
      >
        <X size={14} />
      </button>

      <div className="p-5">
        <div className="flex items-start gap-3.5">
          {/* Icon */}
          <div
            className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center mt-0.5 relative"
            style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}
          >
            <Icon size={17} style={{ color: cfg.accent }} />
            {/* Blinking dot for warning / maintenance */}
            {(popup.type === 'warning' || popup.type === 'maintenance') && (
              <motion.span
                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-[#0d1424]"
                style={{ background: cfg.accent }}
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.2, repeat: Infinity }}
              />
            )}
          </div>

          <div className="flex-1 pr-5">
            {/* Type label */}
            <span
              className="inline-block text-[10px] font-mono font-bold tracking-widest uppercase mb-1"
              style={{ color: cfg.accent }}
            >
              {cfg.label}
            </span>
            <h3 className="font-bold text-white text-sm leading-snug mb-1.5">{popup.title}</h3>
            <p className="text-gray-400 text-xs leading-relaxed">{popup.message}</p>

            {popup.cta_label && popup.cta_url && (
              <a
                href={popup.cta_url}
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold transition-colors"
                style={{ color: cfg.accent }}
              >
                {popup.cta_label} <ArrowRight size={11} />
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function PagePopupManager() {
  const [popups, setPopups] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  const currentPage = window.location.pathname;

  useEffect(() => {
    fetch(`${API_URL}/api/site/popups?page=${encodeURIComponent(currentPage)}`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        const filtered = data.filter(p => {
          if (!p.show_once) return true;
          return !sessionStorage.getItem(`popup_dismissed_${p.id}`);
        });
        setPopups(filtered);
      })
      .catch(() => {});
  }, [currentPage]);

  const dismiss = (popup) => {
    if (popup.show_once) {
      sessionStorage.setItem(`popup_dismissed_${popup.id}`, '1');
    }
    setDismissed(prev => new Set([...prev, popup.id]));
  };

  const dismissAll = () => {
    setPopups(prev => {
      prev.forEach(p => {
        if (p.show_once) sessionStorage.setItem(`popup_dismissed_${p.id}`, '1');
      });
      return prev;
    });
    setDismissed(new Set(popups.map(p => p.id)));
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') dismissAll(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popups]);

  const visible = popups.filter(p => !dismissed.has(p.id));
  if (visible.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9990] flex flex-col items-center justify-center gap-3 p-4"
      style={{
        background: 'radial-gradient(ellipse at 50% 50%, rgba(15,23,42,0.75) 0%, rgba(0,0,0,0.65) 100%)',
        backdropFilter: 'blur(6px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) dismissAll(); }}
      role="dialog"
      aria-modal="true"
    >
      <AnimatePresence>
        {visible.map((popup, i) => (
          <div key={popup.id} className="w-full max-w-md">
            <SinglePopup popup={popup} onClose={() => dismiss(popup)} index={i} />
          </div>
        ))}
      </AnimatePresence>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-gray-700 text-xs mt-1 font-mono"
      >
        Click outside or press ESC to close
      </motion.p>
    </motion.div>
  );
}
